import axios from 'axios';
import { toast } from 'react-toastify';

import customAxios from './Axios';
import { getConfig } from '../../AppConfig';
import {
  setAccessToken,
  setEatzyTokens,
  setSessionToken,
  clearAuth,
} from '../../redux/legacy/reducers/ducks/legacy/AuthorizationDuck';
import * as AuthHubClient from './AuthHubClient';

const config = getConfig();

const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

let refreshPromise = null;

const logoutUser = (store) => {
  store.dispatch(clearAuth());
  refreshPromise = null;
};

const isAuthError = (err) => {
  const status = err?.response?.status;
  const message = err?.response?.data?.message || '';

  if (status === 404 && message === 'token not found') return true;

  if (status === 401) return true;

  if (
    status === 403 &&
    /no access token|token.*expired|unauthorized|invalid token/i.test(message)
  )
    return true;

  return false;
};

const isTokenExpiringSoon = (state) => {
  const expires = state?.eatzyTokenExpires;
  if (!expires) return false;
  const expiresMs = Number(expires) * 1000;
  return Date.now() + TOKEN_REFRESH_BUFFER_MS > expiresMs;
};

export const refreshToken = async (store) => {
  let result = false;

  try {
    const state = store.getState()?.authorization;
    const sessionToken = state?.sessionToken;
    const isSilentAccount = state?.isSilentAccount;

    // Silent/guest accounts have no OAuth link in the Eatzy system, so the
    // Bearer-based /account/authorize call always returns 400 for them.
    // Skip the entire session refresh path and go straight to the eatzy
    // refresh-token path to avoid the ~640ms wasted round-trip.
    if (sessionToken && !isSilentAccount) {
      try {
        const refreshResult = await AuthHubClient.refreshSession(sessionToken);

        if (refreshResult.token) {
          store.dispatch(setSessionToken(refreshResult.token));
        }

        const newToken = refreshResult.token || sessionToken;
        if (!newToken) {
          throw new Error('[Auth] No session token available after refresh');
        }
        const eatzyResult = await AuthHubClient.getEatzyAccessTokenBySession(
          newToken
        );

        if (eatzyResult.success) {
          store.dispatch(
            setEatzyTokens({
              accessToken: eatzyResult.accessToken,
              refreshToken: eatzyResult.refreshToken,
              accessExpires: eatzyResult.accessExpires,
            })
          );
          store.dispatch(
            setAccessToken({
              token: eatzyResult.accessToken,
              expires: eatzyResult.accessExpires,
            })
          );
          refreshPromise = null;
          return true;
        }
      } catch (sessionErr) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Auth] Session refresh failed, trying Eatzy refresh:',
          sessionErr.message
        );
      }
      // Session path did not return a fresh token (threw or returned success:false).
      // The token in Redux may be the same stale one that was just rejected by
      // the API (e.g. email/password users get 400 from /account/authorize since
      // the Bearer flow is only for social accounts). Fall through to the eatzy
      // refresh-token path (/account/refresh) unconditionally.
    }

    const eatzyRefresh =
      store.getState()?.authorization?.eatzyRefreshToken ||
      store.getState()?.authorization?.refreshToken?.token;

    if (!eatzyRefresh) {
      // For silent/guest accounts, fall back to email+password before giving up.
      // guestContactEmail and silentAccountPassword are stored in Redux state
      // during guest registration and can re-authorize without a refresh token.
      if (isSilentAccount) {
        const silentState = store.getState()?.authorization;
        const guestEmail = silentState?.guestContactEmail;
        const guestPassword = silentState?.silentAccountPassword;
        if (guestEmail && guestPassword) {
          const eatzyResult = await AuthHubClient.getEatzyAccessToken(
            guestEmail,
            guestPassword
          );
          if (eatzyResult?.success) {
            store.dispatch(
              setEatzyTokens({
                accessToken: eatzyResult.accessToken,
                refreshToken: eatzyResult.refreshToken,
                accessExpires: eatzyResult.accessExpires,
              })
            );
            store.dispatch(
              setAccessToken({
                token: eatzyResult.accessToken,
                expires: eatzyResult.accessExpires,
              })
            );
            refreshPromise = null;
            return true;
          }
        }
      }
      throw new Error('No refresh token');
    }

    const { data } = await axios.post(
      `${config.apiBaseUrl}/account/refresh`,
      { token: eatzyRefresh },
      { headers: { Authorization: `Basic ${eatzyRefresh}` } }
    );

    store.dispatch(setAccessToken(data));
    refreshPromise = null;
    result = true;
  } catch (err) {
    logoutUser(store);
    result = false;
  }

  return result;
};

export const requestInterceptor = async (request, store) => {
  if (!request.headers) {
    request.headers = {};
  }

  if (!request.headers.Authorization) {
    const state = store.getState()?.authorization;
    const eatzyToken = state?.eatzyAccessToken || state?.accessToken?.token;

    if (eatzyToken && isTokenExpiringSoon(state)) {
      if (!refreshPromise) {
        refreshPromise = refreshToken(store);
      }
      await refreshPromise;
      const freshState = store.getState()?.authorization;
      const freshToken =
        freshState?.eatzyAccessToken || freshState?.accessToken?.token;
      if (freshToken) {
        request.headers.Authorization = `Bearer ${freshToken}`;
      }
    } else if (eatzyToken) {
      request.headers.Authorization = `Bearer ${eatzyToken}`;
    }
  }

  const { useFakeData } = config;

  if (useFakeData === 'true') {
    request.headers['X-API-FakeData'] = 'random';
  }

  request.headers['Content-Type'] = 'application/json';
  request.headers['X-App-Version'] = config.version;
  request.headers['X-App-Source'] =
    config.appType === 'restaurant'
      ? 'restaurant-portal-web'
      : 'consumer-portal-web';
  request.headers['X-App-OS'] =
    window?.navigator?.platform ||
    window?.navigator?.userAgentData?.platform ||
    'unknown';

  return request;
};

export const responseSuccessInterceptor = ({ data }) => data;

const hasAuthTokens = (store) => {
  const state = store.getState()?.authorization;
  return !!(
    state?.sessionToken ||
    state?.eatzyAccessToken ||
    state?.accessToken?.token ||
    state?.eatzyRefreshToken ||
    state?.refreshToken?.token
  );
};

export const responseFailInterceptor = async (err, store) => {
  // eslint-disable-next-line no-underscore-dangle
  if (isAuthError(err) && !err?.config?._authRetry) {
    const wasLoggedIn = hasAuthTokens(store);

    if (!wasLoggedIn) {
      return Promise.reject(err?.response);
    }

    if (!refreshPromise) {
      refreshPromise = refreshToken(store);
    }
    const isReseted = await refreshPromise;
    if (isReseted) {
      // eslint-disable-next-line no-underscore-dangle
      const updatedConfig = { ...err.config, _authRetry: true };
      const state = store.getState()?.authorization;
      const token = state?.eatzyAccessToken || state?.accessToken?.token;
      if (token) {
        updatedConfig.headers = {
          ...updatedConfig.headers,
          Authorization: `Bearer ${token}`,
        };
      }
      return customAxios(updatedConfig);
    }

    toast.warning('Your session has expired. Please log in to continue.', {
      toastId: 'SessionExpired',
      autoClose: 5000,
    });

    return Promise.reject(err?.response);
  }
  return Promise.reject(err?.response);
};

export const setupInterceptors = (store) => {
  customAxios.interceptors.request.use((request) =>
    requestInterceptor(request, store)
  );
  customAxios.interceptors.response.use(
    responseSuccessInterceptor,
    async (err) => responseFailInterceptor(err, store)
  );

  return customAxios;
};
